import type { ChatUser } from '../../types/chat'
import type { UserProfile } from '../../services/appsync-client'
import type {
  OnCallBookableSlot,
  OnCallCustomBlock,
  OnCallOrgConfig,
  OnCallPeriod,
  OnCallRecurringBlock,
} from '@shared/on-call/types'
import {
  DAYTIME_BOOKINGS_KEY,
  DAYTIME_ORG_CONFIG_KEY,
  type DaytimeContactPeriod,
  type DaytimeOrgConfig,
} from '@shared/daytime-contact/types'
import { ON_CALL_DEFAULT_HOURS, ON_CALL_SLOT_CAPACITY } from '@shared/on-call/types'
import {
  userCanBeOnCall,
  userIsDaytimeCallReceiver,
  userIsUrgentHelpReceiver,
} from '@shared/user-settings/flags'
import { resolveProviderLevel } from '../../utils/rbac'
import { isProvider } from '../../utils/user-role-label'

export type BookableWindow = {
  id: string
  blockType: 'recurring' | 'custom'
  startTime: string
  endTime: string
  label: string
  maxConcurrent?: number
}

function parseSettingsRoot(raw: unknown): Record<string, unknown> {
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as Record<string, unknown>
    } catch {
      return {}
    }
  }
  return raw as Record<string, unknown>
}

function migrateLegacySlots(slots: OnCallBookableSlot[]): OnCallCustomBlock[] {
  return slots.map((slot) => ({
    id: slot.id,
    startTime: slot.startTime,
    endTime: slot.endTime,
    maxConcurrent: slot.maxConcurrent,
    notes: slot.notes,
  }))
}

function normalizeOrgConfig(raw: OnCallOrgConfig | undefined): OnCallOrgConfig | null {
  if (!raw) return null
  const recurringBlocks = (raw.recurringBlocks ?? []).map(normalizeRecurringBlock)
  const customBlocks = [
    ...(raw.customBlocks ?? []),
    ...(raw.slots?.length ? migrateLegacySlots(raw.slots) : []),
  ]
  if (!recurringBlocks.length && !customBlocks.length) return null
  return {
    recurringBlocks,
    customBlocks,
    updatedAt: raw.updatedAt,
    updatedBy: raw.updatedBy,
  }
}

export function canManageOnCallSlots(
  profile: Pick<UserProfile, 'roleType' | 'providerLevel'>,
): boolean {
  return isProvider(profile) && resolveProviderLevel(profile) >= 4
}

export function canAccessOnCallSettings(
  profile: Pick<UserProfile, 'roleType' | 'providerLevel'>,
): boolean {
  return isProvider(profile) && resolveProviderLevel(profile) >= 3
}

/**
 * Admin- and owner-level providers (level 3+: "Provider Admin" / "Provider
 * Owner") may see who is scheduled across every on-call block — not just their
 * own bookings. Basic providers below this level only ever see their own.
 */
export function canViewOnCallCoverage(
  profile: Pick<UserProfile, 'roleType' | 'providerLevel'>,
): boolean {
  return isProvider(profile) && resolveProviderLevel(profile) >= 3
}

export function getOnCallBookings(
  user: Pick<ChatUser, 'settingsJson' | 'userId'> | Pick<UserProfile, 'settingsJson' | 'userId'>,
): OnCallPeriod[] {
  const root = parseSettingsRoot(user.settingsJson)
  const bookings = root.onCallBookings as OnCallPeriod[] | undefined
  if (!Array.isArray(bookings)) return []
  return bookings.filter((b) => b.providerId === user.userId)
}

export function getOnCallOrgConfig(
  user: { settingsJson?: unknown } | null | undefined,
): OnCallOrgConfig | null {
  if (!user) return null
  const root = parseSettingsRoot(user.settingsJson)
  const config = root.onCallOrgConfig as OnCallOrgConfig | undefined
  return normalizeOrgConfig(config)
}

/** Merge org config from level-4 admins (first non-empty wins for beta). */
type SettingsUser = { settingsJson?: unknown; roleType?: string | null; providerLevel?: number | null }

export function resolveOrgOnCallConfig(users: SettingsUser[]): OnCallOrgConfig | null {
  for (const user of users) {
    if (resolveProviderLevel(user) < 4) continue
    const config = getOnCallOrgConfig(user)
    if (config) return config
  }
  return null
}

function padTime(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

function normalizeRecurringBlock(block: OnCallRecurringBlock): OnCallRecurringBlock {
  const start = block.dayOfWeekStart ?? block.dayOfWeek ?? 1
  const end = block.dayOfWeekEnd ?? block.dayOfWeek ?? start
  return { ...block, dayOfWeekStart: start, dayOfWeekEnd: end }
}

function daysBetween(startDay: number, endDay: number): number {
  let diff = endDay - startDay
  if (diff < 0) diff += 7
  return diff
}

function formatRecurringLabel(block: OnCallRecurringBlock): string {
  const b = normalizeRecurringBlock(block)
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const startDay = days[b.dayOfWeekStart] ?? 'Day'
  const endDay = days[b.dayOfWeekEnd] ?? 'Day'
  const startTime = padTime(b.startHour, b.startMinute)
  const endTime = padTime(b.endHour, b.endMinute)
  if (b.dayOfWeekStart === b.dayOfWeekEnd) {
    return `${startDay} ${startTime}–${endTime}`
  }
  return `${startDay} ${startTime} → ${endDay} ${endTime} (overnight)`
}

function expandRecurringBlockForDate(
  block: OnCallRecurringBlock,
  date: Date,
): BookableWindow | null {
  const b = normalizeRecurringBlock(block)
  if (date.getDay() !== b.dayOfWeekStart) return null

  const start = new Date(date)
  start.setHours(b.startHour, b.startMinute, 0, 0)

  const span = daysBetween(b.dayOfWeekStart, b.dayOfWeekEnd)
  const end = new Date(start)
  end.setDate(end.getDate() + span)
  end.setHours(b.endHour, b.endMinute, 0, 0)

  if (span === 0 && end.getTime() <= start.getTime()) return null
  if (end.getTime() <= start.getTime()) return null

  const dateFmt = new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
  return {
    id: `${b.id}-${start.toISOString().slice(0, 10)}`,
    blockType: 'recurring',
    startTime: start.toISOString(),
    endTime: end.toISOString(),
    label: `${dateFmt.format(start)} · ${formatRecurringLabel(b)}`,
    maxConcurrent: b.maxConcurrent,
  }
}

/** Upcoming bookable windows from org config for a date range (inclusive day count). */
export function getBookableWindowsForRange(
  config: OnCallOrgConfig | null,
  rangeStart: Date,
  dayCount: number,
  now: Date = new Date(),
): BookableWindow[] {
  if (!config) return []
  const windows: BookableWindow[] = []
  const startDay = new Date(rangeStart)
  startDay.setHours(0, 0, 0, 0)
  const rangeEnd = new Date(startDay)
  rangeEnd.setDate(rangeEnd.getDate() + dayCount)

  for (let offset = 0; offset < dayCount; offset++) {
    const day = new Date(startDay)
    day.setDate(day.getDate() + offset)

    for (const block of config.recurringBlocks) {
      const expanded = expandRecurringBlockForDate(block, day)
      if (expanded && Date.parse(expanded.endTime) > now.getTime()) {
        windows.push(expanded)
      }
    }
  }

  for (const block of config.customBlocks) {
    if (Date.parse(block.endTime) <= now.getTime()) continue
    const start = new Date(block.startTime)
    const end = new Date(block.endTime)
    if (end <= startDay || start >= rangeEnd) continue
    const dateFmt = new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
    const timeFmt = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' })
    windows.push({
      id: block.id,
      blockType: 'custom',
      startTime: block.startTime,
      endTime: block.endTime,
      label: `${dateFmt.format(start)} · ${timeFmt.format(start)} – ${timeFmt.format(end)}`,
      maxConcurrent: block.maxConcurrent,
    })
  }

  return windows.sort((a, b) => Date.parse(a.startTime) - Date.parse(b.startTime))
}

export function countOverlappingBookings(
  periodStart: string,
  periodEnd: string,
  allBookings: OnCallPeriod[],
  excludeProviderId?: string,
): number {
  const start = Date.parse(periodStart)
  const end = Date.parse(periodEnd)
  const providers = new Set<string>()
  for (const booking of allBookings) {
    if (excludeProviderId && booking.providerId === excludeProviderId) continue
    const bStart = Date.parse(booking.startTime)
    const bEnd = Date.parse(booking.endTime)
    if (bStart < end && bEnd > start) providers.add(booking.providerId)
  }
  return providers.size
}

/** Coverage of a single bookable window: who booked it and how many slots remain open. */
export type WindowCoverage = {
  window: BookableWindow
  capacity: number
  /** Distinct providers whose booking overlaps this window, in booking order. */
  bookedProviderIds: string[]
  /** Slots still unfilled (capacity − booked); 0 when fully covered. */
  openCount: number
}

/**
 * Resolve how many providers a window may hold. On-call windows are always
 * single-occupancy ({@link ON_CALL_SLOT_CAPACITY}); other domains (daytime HQ
 * contact) fall back to the legacy per-block `maxConcurrent` (defaulting to 2).
 */
export const resolveOnCallCapacity = (): number => ON_CALL_SLOT_CAPACITY
export const resolveDaytimeCapacity = (window: BookableWindow): number =>
  window.maxConcurrent ?? 2

/**
 * Overlay every bookable window with the providers who have booked it.
 *
 * A booking covers a window when its time range overlaps the window (the same
 * rule used for booking fullness), deduped per provider. Used by the admin /
 * owner coverage overview to surface filled vs. open on-call blocks.
 *
 * `resolveCapacity` controls how many providers a window may hold. It defaults
 * to the legacy per-block behavior so daytime coverage is unchanged; on-call
 * callers pass {@link resolveOnCallCapacity} to enforce single occupancy.
 */
export function getCoverageForWindows(
  windows: BookableWindow[],
  allBookings: OnCallPeriod[],
  resolveCapacity: (window: BookableWindow) => number = resolveDaytimeCapacity,
): WindowCoverage[] {
  return windows.map((window) => {
    const start = Date.parse(window.startTime)
    const end = Date.parse(window.endTime)
    const seen = new Set<string>()
    const bookedProviderIds: string[] = []
    for (const booking of allBookings) {
      if (seen.has(booking.providerId)) continue
      const bStart = Date.parse(booking.startTime)
      const bEnd = Date.parse(booking.endTime)
      if (bStart < end && bEnd > start) {
        seen.add(booking.providerId)
        bookedProviderIds.push(booking.providerId)
      }
    }
    const capacity = resolveCapacity(window)
    return {
      window,
      capacity,
      bookedProviderIds,
      openCount: Math.max(0, capacity - bookedProviderIds.length),
    }
  })
}

/** Stable identity for a bookable window / booking time-slot. */
function slotKey(startTime: string, endTime: string): string {
  return `${startTime}__${endTime}`
}

/**
 * Reconcile a provider's bookings against the current org windows.
 *
 * Root cause of stranded bookings: windows (the org template) and bookings
 * (per-user settings) are stored independently and only matched at render time
 * by time range. When an admin deletes or edits a window (e.g. removes Monday
 * for a public holiday, or shifts its hours), the booking row that referenced
 * the now-deleted slot is never touched — it lingers as "ghost" coverage.
 *
 * This treats any FUTURE booking whose time-slot no longer exists in the config
 * (window deleted, or edited so the slot moved) as orphaned and removes it.
 * Past bookings are always kept as history.
 */
export function reconcileBookingsWithWindows(
  config: OnCallOrgConfig | null,
  bookings: OnCallPeriod[],
  now: Date = new Date(),
): { kept: OnCallPeriod[]; removed: OnCallPeriod[] } {
  const nowMs = now.getTime()
  const future = bookings.filter((b) => Date.parse(b.endTime) > nowMs)
  if (future.length === 0) return { kept: bookings, removed: [] }

  const startDay = new Date(now)
  startDay.setHours(0, 0, 0, 0)
  const maxEndMs = Math.max(...future.map((b) => Date.parse(b.endTime)))
  const dayCount = Math.max(7, Math.ceil((maxEndMs - startDay.getTime()) / 86_400_000) + 2)
  const windows = config ? getBookableWindowsForRange(config, startDay, dayCount, now) : []
  const validKeys = new Set(windows.map((w) => slotKey(w.startTime, w.endTime)))

  const kept: OnCallPeriod[] = []
  const removed: OnCallPeriod[] = []
  for (const b of bookings) {
    if (Date.parse(b.endTime) <= nowMs) {
      kept.push(b)
      continue
    }
    if (validKeys.has(slotKey(b.startTime, b.endTime))) kept.push(b)
    else removed.push(b)
  }
  return { kept, removed }
}

export function getAllOnCallBookings(
  users: Array<Pick<ChatUser, 'settingsJson' | 'userId'>>,
): OnCallPeriod[] {
  const periods: OnCallPeriod[] = []
  for (const user of users) {
    periods.push(...getOnCallBookings(user))
  }
  return periods.sort((a, b) => Date.parse(a.startTime) - Date.parse(b.startTime))
}

export function getEligibleOnCallUsers<T extends Pick<ChatUser, 'settingsJson' | 'userId' | 'roleType' | 'providerLevel'>>(
  users: T[],
): T[] {
  return users.filter((u) => isProvider(u) && userCanBeOnCall(u))
}

/**
 * Leadership team rung by the urgent-help flow.
 *
 * Prefers providers explicitly flagged as urgent-help receivers. When none are
 * flagged (e.g. before any admin configures the roster), falls back to the whole
 * level-4 leadership team so urgent help is never a dead end.
 */
export function getUrgentHelpReceivers<T extends Pick<ChatUser, 'settingsJson' | 'userId' | 'roleType' | 'providerLevel'>>(
  users: T[],
): T[] {
  const flagged = users.filter((u) => isProvider(u) && userIsUrgentHelpReceiver(u))
  if (flagged.length > 0) return flagged
  return users.filter((u) => isProvider(u) && resolveProviderLevel(u) >= 4)
}

export function isOutsideOnCallHours(now: Date = new Date()): boolean {
  const hour = now.getHours()
  return hour < ON_CALL_DEFAULT_HOURS.START || hour >= ON_CALL_DEFAULT_HOURS.END
}

/** Who is on-call right now among eligible providers. Outside hours → last scheduled. */
export function getCurrentOnCallProviders(
  users: Array<Pick<ChatUser, 'settingsJson' | 'userId' | 'roleType' | 'providerLevel'>>,
  now: Date = new Date(),
): string[] {
  const eligible = getEligibleOnCallUsers(users)
  const eligibleIds = new Set(eligible.map((u) => u.userId))
  const nowMs = now.getTime()
  const periods = getAllOnCallBookings(users).filter((p) => eligibleIds.has(p.providerId))
  const active = periods.filter((p) => {
    const start = Date.parse(p.startTime)
    const end = Date.parse(p.endTime)
    return start <= nowMs && end > nowMs
  })
  if (active.length > 0) return active.map((p) => p.providerId)

  if (isOutsideOnCallHours(now) && periods.length > 0) {
    const past = periods.filter((p) => Date.parse(p.endTime) <= nowMs)
    if (past.length > 0) return [past[past.length - 1].providerId]
  }
  return []
}

export function findUpcomingOnCallReminder(
  userId: string,
  users: Array<Pick<ChatUser, 'settingsJson' | 'userId'>>,
  now: Date = new Date(),
  withinMinutes = 30,
): OnCallPeriod | null {
  const nowMs = now.getTime()
  const windowEnd = nowMs + withinMinutes * 60_000
  for (const period of getAllOnCallBookings(users)) {
    if (period.providerId !== userId) continue
    const start = Date.parse(period.startTime)
    if (start > nowMs && start <= windowEnd) return period
  }
  return null
}

export function formatOnCallWindow(startIso: string, endIso: string): string {
  const start = new Date(startIso)
  const end = new Date(endIso)
  const sameDay = start.toDateString() === end.toDateString()
  const dateFmt = new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
  const timeFmt = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' })
  if (sameDay) {
    return `${dateFmt.format(start)} · ${timeFmt.format(start)} – ${timeFmt.format(end)}`
  }
  return `${dateFmt.format(start)} ${timeFmt.format(start)} – ${dateFmt.format(end)} ${timeFmt.format(end)}`
}

export function buildOnCallPeriodFromWindow(
  window: BookableWindow,
  providerId: string,
): OnCallPeriod {
  return {
    id: crypto.randomUUID(),
    providerId,
    startTime: window.startTime,
    endTime: window.endTime,
    createdAt: new Date().toISOString(),
    createdBy: providerId,
    blockId: window.id,
  }
}

function normalizeDaytimeOrgConfig(raw: DaytimeOrgConfig | undefined): DaytimeOrgConfig | null {
  if (!raw) return null
  const recurringBlocks = (raw.recurringBlocks ?? []).map(normalizeRecurringBlock)
  const customBlocks = [
    ...(raw.customBlocks ?? []),
    ...(raw.slots?.length ? migrateLegacySlots(raw.slots) : []),
  ]
  if (!recurringBlocks.length && !customBlocks.length) return null
  return {
    recurringBlocks,
    customBlocks,
    updatedAt: raw.updatedAt,
    updatedBy: raw.updatedBy,
  }
}

export function getDaytimeOrgConfig(
  user: { settingsJson?: unknown } | null | undefined,
): DaytimeOrgConfig | null {
  if (!user) return null
  const root = parseSettingsRoot(user.settingsJson)
  const config = root[DAYTIME_ORG_CONFIG_KEY] as DaytimeOrgConfig | undefined
  return normalizeDaytimeOrgConfig(config)
}

export function resolveOrgDaytimeConfig(users: SettingsUser[]): DaytimeOrgConfig | null {
  for (const user of users) {
    if (resolveProviderLevel(user) < 4) continue
    const config = getDaytimeOrgConfig(user)
    if (config) return config
  }
  return null
}

export function getDaytimeBookings(
  user: Pick<ChatUser, 'settingsJson' | 'userId'> | Pick<UserProfile, 'settingsJson' | 'userId'>,
): DaytimeContactPeriod[] {
  const root = parseSettingsRoot(user.settingsJson)
  const bookings = root[DAYTIME_BOOKINGS_KEY] as DaytimeContactPeriod[] | undefined
  if (!Array.isArray(bookings)) return []
  return bookings.filter((b) => b.providerId === user.userId)
}

export function getAllDaytimeBookings(
  users: Array<Pick<ChatUser, 'settingsJson' | 'userId'>>,
): DaytimeContactPeriod[] {
  const periods: DaytimeContactPeriod[] = []
  for (const user of users) {
    periods.push(...getDaytimeBookings(user))
  }
  return periods.sort((a, b) => Date.parse(a.startTime) - Date.parse(b.startTime))
}

export function getEligibleDaytimeReceivers<
  T extends Pick<ChatUser, 'settingsJson' | 'userId' | 'roleType' | 'providerLevel'>,
>(users: T[]): T[] {
  return users.filter((u) => isProvider(u) && userIsDaytimeCallReceiver(u))
}

export function isDaytimeOfficeHours(now: Date = new Date()): boolean {
  const hour = now.getHours()
  return hour >= ON_CALL_DEFAULT_HOURS.START && hour < ON_CALL_DEFAULT_HOURS.END
}

/**
 * Active daytime HQ call receiver when no on-call provider is scheduled.
 * Only applies during office hours (6:00–21:00 local).
 */
export function getCurrentDaytimeContactProviders(
  users: Array<Pick<ChatUser, 'settingsJson' | 'userId' | 'roleType' | 'providerLevel'>>,
  now: Date = new Date(),
): string[] {
  if (!isDaytimeOfficeHours(now)) return []
  const eligible = getEligibleDaytimeReceivers(users)
  const eligibleIds = new Set(eligible.map((u) => u.userId))
  const nowMs = now.getTime()
  const periods = getAllDaytimeBookings(users).filter((p) => eligibleIds.has(p.providerId))
  const active = periods.filter((p) => {
    const start = Date.parse(p.startTime)
    const end = Date.parse(p.endTime)
    return start <= nowMs && end > nowMs
  })
  return active.map((p) => p.providerId)
}

/** Who a non-emergency "Call for Help" call should ring, resolved by coverage. */
export type CallForHelpCoverage = {
  userId: string
  /** 'daytime' = Daytime HQ contact, 'on-call' = on-call team leader. */
  kind: 'daytime' | 'on-call'
}

/**
 * Resolve who to call for a non-000 "Call for Help" request based on the
 * coverage/on-call schedule at the time of the call.
 *
 * Priority (per product spec):
 *   1. Daytime HQ coverage booked & current (only inside office hours) → that user.
 *   2. Otherwise on-call booked & current → the on-call user.
 *   3. Otherwise null (caller falls back to urgent leadership support).
 */
export function resolveCallForHelpCoverage(
  users: Array<Pick<ChatUser, 'settingsJson' | 'userId' | 'roleType' | 'providerLevel'>>,
  now: Date = new Date(),
): CallForHelpCoverage | null {
  const daytime = getCurrentDaytimeContactProviders(users, now)
  if (daytime.length > 0) return { userId: daytime[0], kind: 'daytime' }
  const onCall = getCurrentOnCallProviders(users, now)
  if (onCall.length > 0) return { userId: onCall[0], kind: 'on-call' }
  return null
}
